import { useI18n } from "react-simple-i18n";
import { useState, useRef, useEffect } from "react";
import { styled, alpha } from "@mui/material/styles";
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Avatar } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';

import { Layout } from "../components/layout/layout";
import { TextField } from "../components/inputs/textfield";
import { tempCoverImg } from "../assets/image";
import { GlobalFirstSection, GlobalSpacing } from "../components/layout/styles";
import { FirstButton, FirstOutlineButton, TransparentButton } from "../components/buttons/buttons";
import { apiNotification } from "../services/apiNotification";
import { useGlobalContext } from "../provider";
import { restApi } from "../provider/restApi";
import { capitalizeFirstLetter, toastMessage } from "../utils";

interface EditFormObject {
	name: string
	bio: string
	location: string
	github: string
	linkedin: string
	twitter: string

	errorName: boolean
	errorEmail: boolean
	errorBio: boolean
	errorLocation: boolean
}

const initEditForm: EditFormObject = {
	name: "",
	bio: "",
	location: "",
	github: "",
	linkedin: "",
	twitter: "",

	errorName: false,
	errorEmail: false,
	errorLocation: false,
	errorBio: false
}

const Profile = () => {
	const { t } = useI18n();
	const [avatar, setAvatar] = useState("");
	const [coverImage, setCoverImage] = useState("");
	const [isEditing, setIsEditing] = useState(false);
	const [editForm, setEditForm] = useState<EditFormObject>(initEditForm);
	const [globalState, { dispatch }]: GlobalContextType = useGlobalContext();

	const userData = globalState.userData;
	const coverImageInputRef = useRef<HTMLInputElement>(null);
	const avatarInputRef = useRef<HTMLInputElement>(null);

	const [avatarHover, setAvatarHover] = useState("");
	const [avatarFile, setAvatarFile] = useState<any>(null);
	const [coverImgFile, setCoverImgFile] = useState<any>(null);


	useEffect(() => {
		setAvatar(userData.image);
		setCoverImage(userData.coverImage);
	}, [userData])

	const updateEditFormData = (data: Partial<EditFormObject>) => {
		setEditForm(prev => ({ ...prev, ...data }))
	}

	const checkValidate = (data: EditFormObject): boolean => {
		if (!data.name) return false;
		if (!data.location) return false;
		if (!data.bio) return false;

		return true;
	}

	const handleSave = async () => {
		try {
			const validate = checkValidate(editForm);

			if (!validate) {
				updateEditFormData({
					errorName: !editForm.name,
					errorLocation: !editForm.location,
					errorBio: !editForm.bio,
				})

				return false;
			}

			dispatch({ loading: true });

			const profileData: UpdateProfileProps = {
				name: editForm.name,
				location: editForm.location,
				bio: editForm.bio,
				github: editForm.github,
				linkedin: editForm.linkedin,
				twitter: editForm.twitter,
			}

			const resData = await restApi.updateProfile(profileData);
			dispatch({ loading: false, userData: resData.userData });
			onCloseEditModal();

			dispatch({ loading: false });
			toastMessage("success", "Update profile successed!");
		} catch (err: any) {
			dispatch({ loading: false });
			apiNotification(err, "Profile update failed!");
		}
	}

	const onOpenEditModal = () => {
		setEditForm({
			...initEditForm,

			name: userData.name,
			bio: userData.bio,
			location: userData.location,

			github: userData.github,
			linkedin: userData.linkedin,
			twitter: userData.twitter,
		})

		setIsEditing(true);
	}

	const onCloseEditModal = () => {
		setIsEditing(false);
	}

	const handleCoverImageChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
		const file = target.files?.[0];

		if (file) {
			const reader = new FileReader();
			reader.onloadend = () => {
				setCoverImage(reader.result as string);
				setCoverImgFile(file);
			}

			reader.readAsDataURL(file);
		}
	}

	const handleAvatarChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
		const file = target.files?.[0];

		if (file) {
			// Check file size (max 5MB)
			if (file.size > 5 * 1024 * 1024) {
				alert('File size should be less than 5MB');
				return;
			}

			// Check file type
			if (!file.type.startsWith('image/')) {
				alert('Please upload an image file');
				return;
			}

			const reader = new FileReader();
			reader.onloadend = () => {
				setAvatar(reader.result as string);
				setAvatarFile(file);
			}

			reader.readAsDataURL(file);
		}
	}

	const onSaveAvatar = async () => {
		try {
			const formData = new FormData();
			const avatarImg = avatarInputRef.current?.files?.[0];
			const coverImg = coverImageInputRef.current?.files?.[0];

			if (avatarImg && coverImg) {
				formData.append("image", avatarImg); // File input
				formData.append("coverImage", coverImg); // File input
			} else if (!avatarImg && coverImg) {
				formData.append("coverImage", coverImg); // File input
			} else if (avatarImg && !coverImg) {
				formData.append("image", avatarImg); // File input
			} else {
				return false;
			}

			dispatch({ loading: true });
			const resData = await restApi.updateProfilePhoto(formData);
			const newUserData: UserObject = resData.userData;

			setAvatarFile(null);
			setCoverImgFile(null);
			dispatch({ loading: false, userData: newUserData });
		} catch (err: any) {
			dispatch({ loading: false });
			apiNotification(err, "Profile photo set failed!");
		}
	}

	return (
		<Layout>
			<GlobalSpacing>
				<ProfileWrapper>
					<CoverImageSection>
						<Box className="coverImage" style={{ backgroundImage: `url(${coverImage || tempCoverImg})` }} />

						<input type="file"
							accept="image/*"
							ref={coverImageInputRef}
							onChange={handleCoverImageChange}
						/>

						<CoverImageButton onClick={() => { coverImageInputRef.current?.click() }}>
							<CameraAltIcon fontSize="medium" />
							<Typography variant="subtitle1" component="span">
								{t("profile.changeCover")}
							</Typography>
						</CoverImageButton>
					</CoverImageSection>

					<ContentSection>
						<UserInfoSection>
							<Box display="flex" flexDirection="column" gap={2}>
								<StyledAvatar onMouseLeave={() => setAvatarHover("")}
									onMouseEnter={() => setAvatarHover("true")}
								>
									<Avatar src={avatar} alt={capitalizeFirstLetter(userData.name)} />

									<input type="file"
										accept="image/*"
										ref={avatarInputRef}
										onChange={handleAvatarChange}
									/>

									<AvatarOverlay show={avatarHover} onClick={() => { avatarInputRef.current?.click() }}>
										<CameraAltIcon fontSize="medium" />
									</AvatarOverlay>
								</StyledAvatar>

								{(!!avatarFile || !!coverImgFile) && (
									<FirstOutlineButton fullWidth onClick={onSaveAvatar}>
										<Typography variant="body2">{t("buttons.save")}</Typography>
									</FirstOutlineButton>
								)}
							</Box>

							<InfoDetails>
								<Typography variant="h3">{capitalizeFirstLetter(userData.name)}</Typography>

								<Box display="flex" flexDirection="column" gap={1}>
									<LocationInfo>
										<LocationOnIcon fontSize="small" />
										<Typography variant="body2">{userData.location}</Typography>
									</LocationInfo>

									<Typography variant="body1">
										{userData.bio}
									</Typography>
								</Box>

								<SocialLinks>
									{userData.github && (
										<SocialButton startIcon={<GitHubIcon />}
											href={`https://${userData.github}`}
										>
											<Typography variant="body2">{t("profile.github")}</Typography>
										</SocialButton>
									)}

									{userData.linkedin && (
										<SocialButton startIcon={<LinkedInIcon />}
											href={`https://${userData.linkedin}`}
										>
											<Typography variant="body2">{t("profile.linkedin")}</Typography>
										</SocialButton>
									)}

									{userData.twitter && (
										<SocialButton startIcon={<TwitterIcon />}
											href={`https://${userData.twitter}`}
										>
											<Typography variant="body2">{t("profile.twitter")}</Typography>
										</SocialButton>
									)}
								</SocialLinks>

								<EditButton onClick={onOpenEditModal}>
									<EditIcon fontSize="small" />
									<Typography variant="body1">{t("buttons.edit")}</Typography>
								</EditButton>
							</InfoDetails>
						</UserInfoSection>

						{userData.userType === "instructor" && (
							<EarningsSection>
								<Typography variant="h6">
									{t("profile.earningReport")}
								</Typography>

								<EarningsGrid>
									<EarningsCard>
										<MonetizationOnOutlinedIcon fontSize="large" />

										<Box display="flex" flexDirection="column">
											<Typography variant="h5">${userData.totalEarning}</Typography>
											<Typography variant="body2">{t("profile.totalEarning")}</Typography>
										</Box>
									</EarningsCard>

									<EarningsCard>
										<MonetizationOnOutlinedIcon fontSize="large" />
										<Box display="flex" flexDirection="column">
											<Typography variant="h5">${userData.weeklyEarning}</Typography>
											<Typography variant="body2">{t("profile.thisWeek")}</Typography>
										</Box>
									</EarningsCard>

									<EarningsCard>
										<SchoolOutlinedIcon fontSize="large" />
										<Box display="flex" flexDirection="column">
											<Typography variant="h5">{userData.totalStudents}</Typography>
											<Typography variant="body2">{t("profile.totalStudents")}</Typography>
										</Box>
									</EarningsCard>
								</EarningsGrid>
							</EarningsSection>
						)}
					</ContentSection>
				</ProfileWrapper>
			</GlobalSpacing>

			{/* Edit Profile Modal */}
			<Dialog fullWidth
				open={isEditing}
				onClose={onCloseEditModal}
				maxWidth="sm"
			>
				<DialogTitle>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<EditIcon color="primary" fontSize="small" />
						<Typography variant="h6">{t("profile.editProfile")}</Typography>
					</Box>
				</DialogTitle>

				<DialogContent dividers>
					<EditForm>
						<TextField value={editForm.name}
							label={t("inputLabels.name")}
							onChange={({ target }: any) => { updateEditFormData({ name: target.value, errorName: false }) }}
							error={editForm.errorName}
						/>

						<TextField disabled
							defaultValue={userData.email}
						/>

						<TextField value={editForm.location}
							label={t("inputLabels.location")}
							onChange={({ target }: any) => { updateEditFormData({ location: target.value, errorLocation: false }) }}
							error={editForm.errorLocation}
						/>

						<TextField label={t("inputLabels.bio")}
							rows={4} multiline
							value={editForm.bio}
							placeholder={t("inputLabels.bioPlaceHolder")}
							onChange={({ target }: any) => { updateEditFormData({ bio: target.value, errorBio: false }) }}
							helperText={t("inputLabels.bioDescription")}
							error={editForm.errorBio}
						/>

						<Box sx={{ display: 'flex', gap: 1 }}>
							<TextField label={t("profile.github")}
								value={editForm.github}
								onChange={({ target }: any) => { updateEditFormData({ github: target.value }) }}
							/>

							<TextField label={t("profile.linkedin")}
								value={editForm.linkedin}
								onChange={({ target }: any) => { updateEditFormData({ linkedin: target.value }) }}
							/>
						</Box>

						<TextField label={t("profile.twitter")}
							value={editForm.twitter}
							onChange={({ target }: any) => { updateEditFormData({ twitter: target.value }) }}
						/>
					</EditForm>
				</DialogContent>

				<DialogActions sx={{ padding: 2 }}>
					<FirstOutlineButton onClick={onCloseEditModal}>
						<CloseIcon fontSize="small" />
						<Typography variant="body1">{t("buttons.cancel")}</Typography>
					</FirstOutlineButton>

					<FirstButton onClick={handleSave}>
						<SaveIcon fontSize="small" />
						<Typography variant="body1">{t("buttons.saveChange")}</Typography>
					</FirstButton>
				</DialogActions>
			</Dialog>
		</Layout>
	)
}

const ProfileWrapper = styled(GlobalFirstSection)(({ theme }) => ({
	display: 'flex',
	flexDirection: "column",
	padding: `${theme.spacing(5)} 0px`,

	[theme.breakpoints.down("md")]: {
		padding: `${theme.spacing(2)} 0px`,
	}
}))

const CoverImageSection = styled(Box)(({ theme }) => ({
	position: 'relative',

	height: 250,
	borderRadius: 8,
	overflow: 'hidden',

	".coverImage": {
		width: '100%',
		height: '100%',
		backgroundSize: 'cover',
		backgroundPosition: 'center',
	},

	input: {
		display: "none"
	},

	button: {
		opacity: 0,
		transition: 'all 0.3s ease',
	},

	"&:hover": {
		button: {
			opacity: 1,
		},
	},

	[theme.breakpoints.down("md")]: {
		height: 150,

		button: {
			opacity: 1,
		}
	},
}))

const CoverImageButton = styled(TransparentButton)(({ theme }) => ({
	position: 'absolute',
	top: 16, right: 16,
	color: theme.palette.common.white,
	backgroundColor: `${theme.palette.common.firstColor}a0`,

	span: {
		color: theme.palette.common.white,
	},

	'&:hover': {
		backgroundColor: `${theme.palette.common.firstColor}c0`,
	},

	[theme.breakpoints.down("md")]: {
		padding: 8,
		borderRadius: "50%",
		minWidth: "unset",

		span: {
			display: "none",
		}
	}
}))

const ContentSection = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(3),
	padding: `0 ${theme.spacing(3)}`,
	marginTop: -50,
	zIndex: 1,

	[theme.breakpoints.down("md")]: {
		marginTop: -45,
		gap: theme.spacing(2),
		padding: `0 ${theme.spacing(0)}`,
	}
}))

const UserInfoSection = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: "row",
	gap: theme.spacing(3),

	[theme.breakpoints.down('md')]: {
		flexDirection: 'column',
		gap: theme.spacing(2),
	},
}))

const StyledAvatar = styled(Box)(({ theme }) => ({
	position: "relative",
	width: 120, height: 120,

	borderRadius: "50%",
	border: `4px solid ${theme.palette.common.white}`,
	boxShadow: `0px 1px 5px 2px ${theme.palette.common.firstBorder}`,
	transition: 'transform 0.3s ease',

	'&:hover': {
		transform: 'scale(1.02)',
	},

	".MuiAvatar-root": {
		fontWeight: 500,
		fontSize: theme.typography.h2.fontSize,
	},

	".MuiAvatar-root, .MuiAvatar-root img": {
		width: "100%",
		height: "100%",
		borderRadius: "50%",
	},

	input: {
		display: "none",
	},

	[theme.breakpoints.down('md')]: {
		width: 100, height: 100,
		marginLeft: theme.spacing(2),
	}
}))

const AvatarOverlay = styled(Box)<{ show: string }>(({ theme, show = "" }) => ({
	top: 0, left: 0,
	right: 0, bottom: 0,
	position: 'absolute',

	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',

	borderRadius: '50%',
	backgroundColor: alpha(theme.palette.common.firstColor, 0.6),

	opacity: !!show ? 1 : 0,
	transition: 'opacity 0.3s ease',
	cursor: 'pointer',
	zIndex: 1,

	'& .MuiSvgIcon-root': {
		color: theme.palette.common.secondColor,
	},
}))

const InfoDetails = styled(Box)(({ theme }) => ({
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(2),

	p: {
		color: `${theme.palette.common.firstColor}c0`,
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(0),
	}
}))

const LocationInfo = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(0.5),
	marginTop: theme.spacing(1),

	'& .MuiSvgIcon-root': {
		color: `${theme.palette.common.firstColor}c0`,
	}
}))

const SocialLinks = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexWrap: 'wrap',
	gap: theme.spacing(2),
	marginTop: theme.spacing(1),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
	}
}))

const SocialButton = styled(Button)(({ theme }) => ({
	borderRadius: theme.spacing(0.5),
	backgroundColor: theme.palette.common.mainBg,
	border: `1px solid ${theme.palette.common.firstBorder}`,
	padding: theme.spacing(1, 2),
	textTransform: 'none',

	'& .MuiSvgIcon-root': {
		color: `${theme.palette.common.firstColor}c0`,
	},

	p: {
		fontWeight: 500,
		color: `${theme.palette.common.firstColor}c0`,
	},

	'&:hover': {
		backgroundColor: theme.palette.common.firstBorder,
	},

	[theme.breakpoints.down('sm')]: {
		flex: '1 1 auto',
		justifyContent: 'flex-start',
	}
}))

const EditButton = styled(TransparentButton)(({ theme }) => ({
	marginTop: theme.spacing(1),

	p: {
		fontWeight: 400,
		color: `${theme.palette.common.thirdColor} !important`,
	},
}))

const EarningsSection = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(1),

	padding: theme.spacing(3),
	borderRadius: theme.spacing(1),
	backgroundColor: theme.palette.background.paper,
	boxShadow: `0px 1px 5px ${theme.palette.common.firstBorder}`,

	[theme.breakpoints.down("md")]: {
		padding: theme.spacing(2),
		borderRadius: theme.spacing(0.5),
	}
}))

const EarningsGrid = styled(Box)(({ theme }) => ({
	display: 'grid',
	gap: theme.spacing(2),
	gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
	}
}))

const EarningsCard = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(2),
	padding: theme.spacing(3),
	backgroundColor: theme.palette.common.mainBg,

	'& .MuiSvgIcon-root': {
		color: theme.palette.common.thirdColor,
	},

	h5: {
		fontWeight: 600,
	},

	p: {
		fontWeight: 500,
		color: alpha(theme.palette.common.firstColor, 0.75),
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
		padding: theme.spacing(1),

		'& .MuiSvgIcon-root': {
			fontSize: 25,
			color: theme.palette.common.thirdColor,
		},
	}
}))

const EditForm = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(3),
	padding: theme.spacing(1),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(2),
		padding: theme.spacing(0),
	}
}))

export { Profile }